import { useEffect, useState, type FormEvent } from 'react';
import { CheckCircle2, Eye, EyeOff, Loader2 } from 'lucide-react';
import { resetPassword } from '@/services/auth';
import { extractErrorMessage } from '@/services/api';

// ResetPasswordScreen is shown when the user follows an emailed reset
// link. The token lives in the URL hash as "#reset-password?token=abc…".
// We parse it once on mount — re-reading the hash on every render would
// break back-navigation.
export function ResetPasswordScreen() {
  const [token, setToken] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const hash = window.location.hash;
    const q = hash.split('?')[1] ?? '';
    const params = new URLSearchParams(q);
    const t = params.get('token');
    setToken(t);
    if (!t) {
      setError('This reset link is missing a token. Request a new one.');
    }
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!token) {
      setError('Missing reset token');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      await resetPassword(token, password);
      setSuccess(true);
    } catch (err) {
      setError(extractErrorMessage(err, 'Reset failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const toSignIn = () => {
    window.location.hash = '';
  };

  if (success) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-6 p-6">
        <div className="card w-full max-w-sm p-5 text-center space-y-4">
          <div className="flex justify-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-500/10 text-green-600">
              <CheckCircle2 className="h-6 w-6" />
            </div>
          </div>
          <h2 className="text-sm font-medium">Password updated</h2>
          <p className="text-xs text-text-secondary">
            Sign in with your new password to continue.
          </p>
          <button type="button" onClick={toSignIn} className="btn-primary w-full">
            Go to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col items-center justify-center gap-6 p-6">
      <div className="flex flex-col items-center gap-2">
        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary font-bold text-lg">
          U
        </div>
        <h1 className="text-xl font-semibold">Choose a new password</h1>
        <p className="text-sm text-text-secondary text-center max-w-xs">
          Minimum 8 characters with upper and lower case, a digit, and a special character.
        </p>
      </div>

      <form onSubmit={onSubmit} className="card w-full max-w-sm p-5 space-y-4">
        <div className="space-y-1">
          <label className="text-xs font-medium text-text-secondary">New password</label>
          <div className="relative">
            <input
              type={showPw ? 'text' : 'password'}
              autoComplete="new-password"
              className="input pr-10"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              minLength={8}
              maxLength={200}
              disabled={!token}
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              className="absolute inset-y-0 right-2 flex items-center text-text-secondary hover:text-text-primary"
              aria-label={showPw ? 'Hide password' : 'Show password'}
            >
              {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium text-text-secondary">Confirm password</label>
          <input
            type={showPw ? 'text' : 'password'}
            autoComplete="new-password"
            className="input"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="••••••••"
            required
            minLength={8}
            maxLength={200}
            disabled={!token}
          />
        </div>

        {error && (
          <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-900/30 dark:text-red-300">
            {error}
          </div>
        )}

        <button type="submit" disabled={submitting || !token} className="btn-primary w-full">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update password'}
        </button>

        <button type="button" onClick={toSignIn} className="btn-ghost text-xs w-full">
          Back to sign in
        </button>
      </form>
    </div>
  );
}
